/// <amd-module name="Test/TestCaseResults/Component/TestCaseResultsItemProvider" />
import * as Resources from "Test/TestCaseResults/Resources";
import { getDisplayName } from "Test/TestCaseResults/Utils";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { ObservableArray } from "VSS/Core/Observable";
import { IListSelection, ISimpleListCell } from "VSSUI/List";
import { ago, AgoFormat } from "VSSUI/Utilities/Date";

export interface IResultsTableItem {
    OutcomeColumn: ISimpleListCell;
    TimestampColumn: string;
    ConfigurationColumn: string;
    RunbyColumn: string;
    TesterColumn: string;
    TestPlanColumn: string;
    TestSuiteColumn: string;
}

export interface IResultsTableMetadataItem {
    pointId: number;
    runId: number;
    resultId: number;
}

export interface IResultsTableContainer {
    resultsTableItem: ObservableArray<IResultsTableItem>;
    metadata: ObservableArray<IResultsTableMetadataItem>;
}

export interface ITestCaseResultsFilterSelection {
    outcome: IListSelection;
    tester: IListSelection;
    configuration: IListSelection;
    plan: IListSelection;
    suite: IListSelection;
}

export class TestCaseResultsItemProvider {
    private _items: IResultsTableContainer;

    constructor() {
        this._items = {} as IResultsTableContainer;
        this._items.resultsTableItem = new ObservableArray<IResultsTableItem>();
        this._items.metadata = new ObservableArray<IResultsTableMetadataItem>();
    }

    public getTestCaseResultsForTable(testcaseResults: Contracts.TestCaseAssociatedResult[]): IResultsTableContainer {
        // This will be repopulated
        this._items.resultsTableItem.removeAll();
        this._items.metadata.removeAll();

        // Return if there are no test cases
        if (!testcaseResults || testcaseResults.length == 0) {
            return this._items;
        }

        for (let i = 0; i < testcaseResults.length; i++) {
            const outcome = this._getOutcomeDetails(testcaseResults[i].outcome);

            // When results get reset, they won't be shown, same as the current experience.
            if (outcome.text !== Resources.Active) {
                const testPointTableItem: IResultsTableItem = {
                    TestSuiteColumn: testcaseResults[i].suite.name ? testcaseResults[i].suite.name : Resources.Deleted,
                    TestPlanColumn: testcaseResults[i].plan.name,
                    TesterColumn: getDisplayName(testcaseResults[i].tester),
                    RunbyColumn: getDisplayName(testcaseResults[i].runBy),
                    OutcomeColumn: outcome,
                    ConfigurationColumn: testcaseResults[i].configuration.name ? testcaseResults[i].configuration.name : Resources.Deleted,
                    TimestampColumn: ago(testcaseResults[i].completedDate, AgoFormat.Compact)
                };
                const metadata: IResultsTableMetadataItem = {
                    pointId: testcaseResults[i].pointId,
                    runId: testcaseResults[i].runId,
                    resultId: testcaseResults[i].resultId
                };

                this._items.resultsTableItem.push(testPointTableItem);
                this._items.metadata.push(metadata);
            }
        }
        return this._items;
    }

    public _getOutcomeDetails(pointOutcome: Contracts.UserFriendlyTestOutcome): ISimpleListCell {
        let details: ISimpleListCell = {} as ISimpleListCell;

        if (pointOutcome === Contracts.UserFriendlyTestOutcome.Passed) {
            details = { iconProps: { iconName: "CompletedSolid", className: "pass-icon" }, text: Resources.Passed };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Failed) {
            details = { iconProps: { iconName: "StatusErrorFull", className: "fail-icon" }, text: Resources.Failed };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Blocked) {
            details = { iconProps: { iconName: "Blocked2Solid", className: "block-icon" }, text: Resources.Blocked };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.NotApplicable) {
            details = { iconProps: { iconName: "BlockedSolid", className: "not-applicable-icon" }, text: Resources.NotApplicable };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Paused) {
            details = { iconProps: { iconName: "CirclePauseSolid", className: "paused-icon" }, text: Resources.Paused };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.InProgress) {
            details = { iconProps: { iconName: "AwayStatus", className: "in-progress-icon" }, text: Resources.InProgress };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Timeout) {
            details = { iconProps: { iconName: "Clock", className: "timed-out-icon" }, text: Resources.Timeout };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Warning) {
            details = { iconProps: { iconName: "Warning", className: "warning-icon" }, text: Resources.Warning };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Error) {
            details = { iconProps: { iconName: "StatusErrorFull", className: "error-icon" }, text: Resources.Error };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.NotExecuted) {
            details = { iconProps: { iconName: "NotExecuted", className: "not-executed-icon" }, text: Resources.NotExecuted };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Inconclusive) {
            details = { iconProps: { iconName: "BlockedSolid", className: "inconclusive-icon" }, text: Resources.Inconclusive };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Aborted) {
            details = { iconProps: { iconName: "CircleStopSolid", className: "aborted-icon" }, text: Resources.Aborted };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.None) {
            details = { iconProps: { iconName: "", className: "none-icon" }, text: Resources.None }; // TODO: add the icons for these
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.Unspecified) {
            details = { iconProps: { iconName: "", className: "unspecified-icon" }, text: Resources.Unspecified };
        } else if (pointOutcome === Contracts.UserFriendlyTestOutcome.NotImpacted) {
            details = { iconProps: { iconName: "", className: "not-impacted-icon" }, text: Resources.NotImpacted };
        } else {
            details = { iconProps: { iconName: "CircleFill", className: "active-icon" }, text: Resources.Active };
        }
        return details;
    }
}
